import ConditionalWrapper from "@splitfire-agency/raiden-library/dist/components/ConditionalWrapper";
import encodeFormData from "../../../libraries/utils/encodeFormData";
import { useCallback } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useApiError } from "../../../components/ReactHookForm/ApiError";
import useRequest from "../../../hooks/useRequest";
import generateApiUrl from "../../../libraries/utils/generateApiUrl";

/**
 * @typedef {object} ChannelManagerMeetingsFormValues
 * @property {object} data
 * @property {string} data.message
 */

/**
 * @returns {ChannelManagerMeetingsFormValues}
 */
export function getDefaultValues() {
  /** @type {ChannelManagerMeetingsFormValues} */
  return {
    data: {
      message: "",
    },
  };
}

/**
 * @typedef {object} Props
 * @property {() => void} [onSuccess]
 * @property {boolean} [withFormTag]
 * @property {(params: {form: import("react-hook-form").UseFormReturn<ChannelManagerMeetingsFormValues>, submit: () => void, isLoading: boolean}) => import("react").ReactElement} children
 */
/**
 * @param {Props} props
 */
export function ChannelManagerMeetingsHandler({
  onSuccess,
  withFormTag = true,
  children,
}) {
  /** @type {import("react-hook-form").UseFormReturn<ChannelManagerMeetingsFormValues>} */
  const form = useForm({
    defaultValues: getDefaultValues(),
    shouldUseNativeValidation: false,
  });
  const { handleSubmit } = form;

  const { request, isLoading } = useRequest();

  const { resolveApiError, resetApiErrors } = useApiError();

  const submit = useCallback(
    (event) => {
      event.preventDefault();
      handleSubmit(async (values) => {
        const payload = {
          data: {
            ...values.data,
          },
        };

        request(
          `${generateApiUrl({
            id: "@channelManagerMeetings.create",
          })}/`,
          {
            method: "POST",
            body: encodeFormData(payload),
          },
          {
            inputMode: "formData",
          },
        )
          .then((response) => {
            resetApiErrors();
            onSuccess?.();
          })
          .catch((response) => {
            resetApiErrors();
            resolveApiError({ response, form });
          });
      })();
    },
    [form, handleSubmit, onSuccess, request, resetApiErrors, resolveApiError],
  );

  return (
    <FormProvider {...form}>
      <ConditionalWrapper
        hasWrapper={withFormTag}
        wrapper={(children) => <form onSubmit={submit}>{children}</form>}>
        {children({ form, submit, isLoading })}
      </ConditionalWrapper>
    </FormProvider>
  );
}
